import _ from 'lodash'
import moment from 'moment'

import VALUE from 'core/data-model/constants/values'
import { convertEntryAge } from 'core/service/insured/birthdate'
import { getToggles } from 'quick-quote/feature-toggles'

export const isRequiredLicenses = (displayProduct) =>
  displayProduct.licenses !== null &&
  displayProduct.licenses !== undefined &&
  displayProduct.licenses.length > 0 &&
  displayProduct.category === 'INVESTMENT'

/**
 * @param basicPlanRepository {ReturnType<import('core/data-model/basic-plan/repository').createBasicPlanRepository>}
 * @param campaignRepository {ReturnType<import('core/data-model/campaign/repository').createCampaignRepository>}
 */
export function createDisplayProductService(basicPlanRepository, campaignRepository) {
  const getRiderCode = (rider) => rider.code

  const transformProductToDisplayProduct = (product) => {
    switch (product.category) {
      case VALUE.LOAN:
        return {
          code: product.productCode,
          name: product.name,
          nameThai: product.nameThai,
          displayName: product.displayName,
          basicPlanCode: product.productCode,
          basicPlanName: product.name,
          category: VALUE.LOAN,
          description: product.description,
          image: product.image,
          type: product.type,
          benefits: product.benefits,
          readOnlyFields: product.readOnlyFields || {},
          riders: product.riders ? product.riders.map((riderCode) => riderCode.replace('rider_', '')) : [],
          coveragePeriod: product.coveragePeriod,
          paymentPeriod: product.paymentPeriod,
          modelFactors: product.modelFactors,
          optionalRiders: [],
          requiredRiders: [],
          licenses: product.licenses,
          planCodeSchema: product.planCodeSchema ? product.planCodeSchema : [],
          aura: product.aura,
          ribbonMessage: product.ribbonMessage || '',
          payerSelection: product.payerSelection || [],
          allowUserType: product.allowUserType || [],
          defaultSumAssured: product.defaultSumAssured || '',
          basicPlanCodeOption: product.basicPlanCodeOption || [],
          brochureLink: product.brochureLink || {},
          luckyNumberPolicy: product.luckyNumberPolicy || {},
          pensionAge: product.pensionAge || '',
        }
      case VALUE.INVESTMENT:
        // $FlowFixMe
        return {
          code: product.productCode,
          name: product.name,
          nameThai: product.nameThai,
          saleSheet: product.saleSheet,
          displayName: product.displayName,
          fullNameThai: product.fullNameThai,
          displayNameLanguage: product.displayNameLanguage ? product.displayNameLanguage : 'ENG',
          basicPlanCode: product.productCode,
          basicPlanName: product.name,
          category: product.category,
          description: product.description,
          image: product.image,
          type: product.type,
          benefits: product.benefits,
          readOnlyFields: product.readOnlyFields || {},
          riders: product.riders ? product.riders.map((riderCode) => riderCode.replace('rider_', '')) : [],
          coveragePeriod: product.coveragePeriod,
          paymentPeriod: product.paymentPeriod,
          modelFactors: product.modelFactors,
          optionalRiders: product.optionalRiders || [],
          requiredRiders: product.requiredRiders || [],
          licenses: product.licenses,
          defaults: product.defaults,
          entryAge: {
            minimum: convertEntryAge(product.insuredEntryAgeMinimum || ''),
            maximum: convertEntryAge(product.insuredEntryAgeMaximum || ''),
          },
          entrySumAssured: {
            minimum: product.sumAssuredMinimum,
            maximum: product.sumAssuredMaximum,
          },
          validOccupationFactors: product.validOccupationFactors || [],
          rates: product.rates || {},
          feeStructure: product.feeStructure || {},
          paymentRanges: product.paymentRanges || {},
          aura: product.aura,
          ribbonMessage: product.ribbonMessage || '',
          payerSelection: product.payerSelection || [],
          allowUserType: product.allowUserType || [],
          defaultSumAssured: product.defaultSumAssured || '',
          basicPlanCodeOption: product.basicPlanCodeOption || [],
          brochureLink: product.brochureLink || {},
          luckyNumberPolicy: product.luckyNumberPolicy || {},
          pensionAge: product.pensionAge || '',
        }
      default:
        // $FlowFixMe
        return {
          code: product.productCode,
          name: product.name,
          nameThai: product.nameThai,
          displayName: product.displayName,
          fullNameThai: product.fullNameThai,
          displayNameLanguage: product.displayNameLanguage ? product.displayNameLanguage : 'ENG',
          basicPlanCode: product.productCode,
          basicPlanName: product.name,
          category: product.category,
          description: product.description,
          type: product.type,
          benefits: product.benefits,
          readOnlyFields: product.readOnlyFields || {},
          riders: product.riders ? product.riders.map((riderCode) => riderCode.replace('rider_', '')) : [],
          coveragePeriod: product.coveragePeriod,
          paymentPeriod: product.paymentPeriod,
          modelFactors: product.modelFactors,
          optionalRiders: product.optionalRiders || [],
          requiredRiders: product.requiredRiders || [],
          licenses: product.licenses,
          defaults: product.defaults,
          productPlans: product.productPlans,
          entryAge: {
            minimum: convertEntryAge(product.insuredEntryAgeMinimum || ''),
            maximum: convertEntryAge(product.insuredEntryAgeMaximum || ''),
          },
          entrySumAssured: {
            minimum: product.sumAssuredMinimum,
            maximum: product.sumAssuredMaximum,
          },
          aura: product.aura,
          image: product.image,
          validOccupationFactors: product.validOccupationFactors || [],
          expiryDate: product.expiryDate,
          ribbonMessage: product.ribbonMessage || '',
          payerSelection: product.payerSelection || [],
          allowUserType: product.allowUserType || [],
          defaultSumAssured: product.defaultSumAssured || '',
          basicPlanCodeOption: product.basicPlanCodeOption || [],
          brochureLink: product.brochureLink || {},
          luckyNumberPolicy: product.luckyNumberPolicy || {},
          campaignDiscount: product.campaignDiscount || {},
          pensionAge: product.pensionAge || '',
        }
    }
  }

  const transformCampaignToDisplayProduct = (campaign) => {
    const allRiders = campaign.riders.optional.concat(campaign.riders.required).map(getRiderCode)
    // $FlowFixMe

    return {
      code: campaign.code,
      name: campaign.name,
      nameThai: campaign.nameThai,
      fullNameThai: campaign.fullNameThai,
      plans: campaign.basicPlan.plans || [],
      selectedCampaignPlan: campaign.selectedCampaignPlan || '',
      displayName: campaign.displayName,
      basicPlanCode: campaign.basicPlan.productCode,
      basicPlanName: campaign.basicPlan.name,
      displayNameLanguage: campaign.displayNameLanguage,
      category: campaign.category || campaign.basicPlan.category,
      description: campaign.description,
      image: campaign.image,
      type: campaign.type,
      benefits: campaign.benefits,
      readOnlyFields: campaign.readOnlyFields || {},
      riders: allRiders,
      coveragePeriod: campaign.basicPlan.coveragePeriod,
      paymentPeriod: campaign.basicPlan.paymentPeriod,
      modelFactors: campaign.basicPlan.modelFactors,
      optionalRiders: campaign.riders.optional.map(getRiderCode),
      requiredRiders: campaign.riders.required.map(getRiderCode),
      entryAge: {
        minimum: convertEntryAge(campaign.basicPlan.insuredEntryAgeMinimum || ''),
        maximum: convertEntryAge(campaign.basicPlan.insuredEntryAgeMaximum || ''),
      },
      entrySumAssured: {
        minimum: campaign.basicPlan.sumAssuredMinimum,
        maximum: campaign.basicPlan.sumAssuredMaximum,
      },
      validOccupationFactors: campaign.basicPlan.validOccupationFactors || [],
      licenses: campaign.licenses,
      aura: campaign.basicPlan.aura,
      ribbonMessage: campaign.ribbonMessage || '',
      payerSelection: campaign.payerSelection || [],
      allowUserType: campaign.allowUserType || [],
      defaultSumAssured: campaign.defaultSumAssured || '',
      basicPlanCodeOption: campaign.basicPlanCodeOption || [],
      brochureLink: campaign.brochureLink || {},
      luckyNumberPolicy: campaign.luckyNumberPolicy || {},
      pensionAge: campaign.pensionAge || '',
    }
  }

  const transformToDisplayProduct = (product) => {
    if (product.type === 'product') {
      return transformProductToDisplayProduct(product)
    } else {
      return transformCampaignToDisplayProduct(product)
    }
  }

  const _sortByCode = (displayProduct, productList) =>
    _.compact(productList.map((code) => _.find(displayProduct, { code })))

  const isValidProduct = (product) => {
    if (product.expiryDate) {
      const expiryDate = moment(product.expiryDate, VALUE.DATE_FORMAT, VALUE.TIME_ZONE)
      const currentDate = moment(new Date()).tz(VALUE.TIME_ZONE)
      return currentDate.isSameOrBefore(expiryDate)
    }
    return true
  }

  const _listDisplayProducts = async (listActiveBasicPlans, listActiveCampaigns, getViewConfiguration) => {
    const displayProduct = await Promise.all([listActiveCampaigns(), listActiveBasicPlans()])
      .then((products) => products.reduce((a, b) => a.concat(b), []))
      .then((products) => products.map(transformToDisplayProduct))
      .then((products) => (getToggles().ENABLE_VALIDATE_PRODUCT_EXPIRED ? products.filter(isValidProduct) : products))
    const productList = await getViewConfiguration().then((conf) => conf.productList)
    return _sortByCode(displayProduct, productList)
  }

  const listDisplayProducts = () =>
    _listDisplayProducts(
      basicPlanRepository.listActiveBasicPlans,
      campaignRepository.listActiveCampaigns,
      basicPlanRepository.getViewConfiguration
    )

  const _getBasicPlan = (getBasicPlan, getCampaign, query) =>
    query.type === 'product'
      ? getBasicPlan(query.code, query.distributorGroup, query.agentCode)
      : getCampaign(
          query.code,
          typeof query.selectedCampaignPlan === 'undefined' ? '' : query.selectedCampaignPlan,
          query.distributorGroup,
          query.agentCode
        ).then((campaign) => campaign.basicPlan)

  const getBasicPlanFromPlanCodeOrCampaignCode = (query) =>
    _getBasicPlan(basicPlanRepository.getBasicPlan, campaignRepository.getCampaign, query)

  return {
    transformProductToDisplayProduct,
    transformCampaignToDisplayProduct,
    transformToDisplayProduct,
    _sortByCode,
    isValidProduct,
    _listDisplayProducts,
    listDisplayProducts,
    _getBasicPlan,
    getBasicPlan: getBasicPlanFromPlanCodeOrCampaignCode,
  }
}
